import {
    PulseStorage,
    SharedConfigurations,
    StorageChangeKeyMatch,
    StorageChangeOrigin,
} from '@pulse/common';
import { Observable, ReplaySubject } from 'rxjs';
import { UUIDService } from './uuid.service';
import { WebToMobileService } from './w2m.service';

export class DeviceIdService {
    private deviceId = '';
    private deviceIdSubject$ = new ReplaySubject<string>(1);

    constructor(
        protected readonly uuid: UUIDService,
        protected readonly storage: PulseStorage, // Persistent Storage
        protected readonly cookieStorage: PulseStorage,
        protected readonly config: SharedConfigurations,
        protected readonly webToMobileService: WebToMobileService,
        protected readonly storageChange: StorageChangeOrigin, // external
    ) {
        //TODO: remove after 2 months of the release
        this.cookieStorage.remove(this.config.keyDeviceId);
        this.registerStorageChangeListeners();

        this.setDeviceId(this.fetchDeviceId().trim());
    }

    getDeviceId(): string {
        return this.deviceId;
    }

    /**
     * Get an observable that emits device ID changes.
     * Only emits if a device ID has been set, and new subscribers get the last emitted value.
     */
    getDeviceId$(): Observable<string> {
        return this.deviceIdSubject$.asObservable();
    }

    setDeviceId(deviceId: string) {
        if (deviceId === this.deviceId || typeof deviceId !== 'string' || !deviceId.trim()) {
            return;
        }

        let refreshDeviceId: string;
        const trimmedDeviceId = deviceId.trim();
        const webToMobileMetadataInfo = this.webToMobileService.getWebToMobileMetaInfo();

        if (webToMobileMetadataInfo?.['device_id']) {
            refreshDeviceId = String(webToMobileMetadataInfo['device_id']);
        } else {
            refreshDeviceId = trimmedDeviceId;
        }

        const currentStored = this.storage.get(this.config.keyDeviceId);

        if (currentStored !== refreshDeviceId) {
            this.storage.set(this.config.keyDeviceId, refreshDeviceId);
        }

        this.deviceId = refreshDeviceId;
        this.deviceIdSubject$.next(refreshDeviceId);
    }

    private generateDeviceId(): string {
        return this.uuid.generate();
    }

    private registerStorageChangeListeners(): void {
        this.storage
            .change(this.config.keyDeviceId, StorageChangeKeyMatch.Full, this.storageChange)
            .subscribe(({ value }) => {
                this.setDeviceId(value || '');
            });
    }

    private fetchDeviceId(): string {
        return this.storage.get(this.config.keyDeviceId) || this.generateDeviceId();
    }
}
